import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Palette,
  Users,
  Package,
  ShieldCheck,
  GraduationCap,
  Home as HomeIcon,
  Award,
  Trees,
  Music,
  Building2,
  Sun as SunIcon,
  Play,
  X,
} from "lucide-react";

const TrustPill = ({ children }) => (
  <div className="inline-flex items-center gap-2 border-2 border-black bg-white px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-black text-black">
    <ShieldCheck size={14} className="text-[#EC008C]" />
    {children}
  </div>
);

const ValueRow = ({ icon, title, body, tint }) => (
  <div className="grid md:grid-cols-12 gap-6 items-start border-t border-black/10 py-8">
    <div className="md:col-span-1">
      <div
        className="w-14 h-14 rounded-full flex items-center justify-center border-2 border-black"
        style={{ background: tint }}
      >
        {icon}
      </div>
    </div>
    <div className="md:col-span-11">
      <h3 className="font-display font-black text-2xl md:text-3xl text-black leading-tight">{title}</h3>
      <p className="mt-3 text-base md:text-lg text-[#3A3A3A] leading-relaxed font-light max-w-3xl">{body}</p>
    </div>
  </div>
);

const KitCard = ({ tag, tint, icon, title, tagline, blurb, trailblazer, testId, slug }) => (
  <article
    data-testid={testId}
    className="group border-2 border-black bg-white flex flex-col overflow-hidden hover:-translate-y-1 transition-transform duration-300"
  >
    <div className="relative aspect-[5/4] flex items-center justify-center" style={{ background: tint }}>
      <div className="absolute top-4 left-4 bg-black text-white text-[10px] tracking-[0.24em] uppercase font-black px-2.5 py-1">
        {tag}
      </div>
      <div className="text-black opacity-80 group-hover:scale-110 transition-transform duration-500">{icon}</div>
    </div>
    <div className="p-6 flex flex-col flex-1">
      <h3 className="font-display font-black text-xl md:text-2xl leading-tight text-black">{title}</h3>
      <div className="mt-1 font-script text-lg text-[#EC008C]">&ldquo;{tagline}&rdquo;</div>
      <p className="mt-4 text-sm text-[#4A4A4A] leading-relaxed font-light flex-1">{blurb}</p>
      <div className="mt-5 pt-4 border-t border-black/10 text-[11px] tracking-[0.22em] uppercase font-semibold text-black">
        Trailblazer · <span className="text-[#EC008C]">{trailblazer}</span>
      </div>
      <Link
        to={`/steam/${slug}`}
        data-testid={`${testId}-cta`}
        className="mt-5 inline-flex items-center justify-between text-[12px] tracking-[0.22em] uppercase font-black text-black border-b-2 border-black pb-2 hover:text-[#EC008C] hover:border-[#EC008C] transition-colors"
      >
        Explore kit <ArrowRight size={14} />
      </Link>
    </div>
  </article>
);

const Steam = () => {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <main data-testid="steam-page" className="bg-white">
      {/* 1 · Hero */}
      <section className="relative overflow-hidden bg-black text-white">
        <video
          data-testid="steam-hero-video"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/steam/kits-lineup.jpg"
        >
          <source src="/steam/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/90" />
        <div className="absolute inset-0 bg-black/25" />
        <div className="relative container-x py-28 md:py-40 max-w-6xl">
          <div className="inline-flex items-center gap-2 bg-[#DCE842] text-black text-[11px] tracking-[0.28em] uppercase font-black px-3 py-1.5 mb-6 border-2 border-black">
            <Sparkles size={12} /> STEAM-2-Go™ · By Makeba Design & Prints
          </div>
          <h1
            className="font-display font-black text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl text-white"
            style={{ textShadow: "0 2px 24px rgba(0,0,0,0.9), 0 0 60px rgba(0,0,0,0.5)" }}
          >
            Where Creativity Meets Innovation:{" "}
            <span className="text-[#DCE842]">Screen-Free STEAM Kits</span> Built for the Next Generation of Trailblazers.
          </h1>
          <p
            className="mt-8 max-w-2xl text-base md:text-lg text-white leading-relaxed font-light"
            style={{ textShadow: "0 1px 12px rgba(0,0,0,0.9)" }}
          >
            Hands-on, portable discovery stations designed with representation at the forefront. We combine Science,
            Technology, Engineering, Art, and Math to inspire curiosity, creativity, and confidence in every child.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 items-center">
            <a
              href="#kits"
              data-testid="steam-cta-shop"
              className="inline-flex items-center gap-2 bg-[#EC008C] text-white px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-[#EC008C] hover:bg-black hover:border-black transition-colors"
            >
              Shop STEAM-2-Go Kits <ArrowRight size={14} />
            </a>
            <a
              href="/steam/steam-2-go-grant-catalog.pdf"
              download="MAKEBA-STEAM-2-Go-Grant-Catalog.pdf"
              target="_blank"
              rel="noreferrer"
              data-testid="steam-cta-grant"
              className="inline-flex items-center gap-2 bg-transparent text-white px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-white hover:bg-white hover:text-black transition-colors"
            >
              Request District / Grant Catalog
            </a>
            <button
              type="button"
              onClick={() => setVideoOpen(true)}
              data-testid="steam-hero-play"
              className="group inline-flex items-center gap-3 text-white text-[12px] tracking-[0.24em] uppercase font-black hover:text-[#DCE842] transition-colors"
            >
              <span className="w-11 h-11 rounded-full bg-[#DCE842] text-black flex items-center justify-center border-2 border-[#DCE842] group-hover:scale-110 transition-transform">
                <Play size={16} fill="currentColor" className="ml-0.5" />
              </span>
              Watch the story
            </button>
          </div>
        </div>
      </section>

      {/* Video modal */}
      {videoOpen && (
        <div
          data-testid="steam-video-modal"
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-8"
          onClick={() => setVideoOpen(false)}
        >
          <button
            type="button"
            data-testid="steam-video-close"
            onClick={() => setVideoOpen(false)}
            className="absolute top-5 right-5 w-12 h-12 rounded-full bg-white text-black flex items-center justify-center hover:bg-[#DCE842] transition-colors z-10"
            aria-label="Close video"
          >
            <X size={22} />
          </button>
          <video
            data-testid="steam-video-modal-player"
            className="w-full max-w-5xl max-h-[85vh] border-2 border-[#DCE842] shadow-2xl"
            src="/steam/hero.mp4"
            controls
            autoPlay
            playsInline
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* 2 · Social Proof */}
      <section data-testid="steam-trust" className="bg-[#F3E30A] border-y-2 border-black">
        <div className="container-x py-8 md:py-10">
          <div className="text-center text-[11px] md:text-[13px] tracking-[0.28em] uppercase font-black text-black mb-5">
            Trusted by K–8 Classrooms · Title I Districts · Libraries &amp; Families Nationwide
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <TrustPill>100% Screen-Free</TrustPill>
            <TrustPill>NGSS &amp; CCSS Aligned</TrustPill>
            <TrustPill>Federal Grant Funding Eligible</TrustPill>
          </div>
        </div>
      </section>

      {/* 3 · Value Proposition */}
      <section className="container-x py-24 md:py-32">
        <div className="max-w-3xl mb-12">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-black mb-4">The Makeba Difference</div>
          <h2 className="font-display font-black text-4xl md:text-6xl leading-[1.02] text-black tracking-tight">
            Full STEAM Ahead: Why Art &amp; Representation Change Everything.
          </h2>
        </div>
        <div>
          <ValueRow
            tint="#DCE842"
            icon={<Palette size={22} className="text-black" strokeWidth={2.5} />}
            title="Art Unlocks Innovation."
            body="We don't just teach formulas; we teach creative problem-solving. By integrating Art into STEM, students translate technical concepts into tangible 3D designs, blueprints, and kinetic artwork they can hold, share, and be proud of."
          />
          <ValueRow
            tint="#EC008C"
            icon={<Users size={22} className="text-white" strokeWidth={2.5} />}
            title="Representation Front &amp; Center."
            body="Children cannot be what they cannot see. Every kit highlights pioneer Black, Indigenous, Latinx, and women trailblazers - from botanist Dr. Wangari Maathai to acoustic pioneer Dr. James E. West - so every child sees themselves as an innovator."
          />
          <ValueRow
            tint="#F3E30A"
            icon={<Package size={22} className="text-black" strokeWidth={2.5} />}
            title="100% Screen-Free &amp; Portable."
            body="Durable, high-quality, self-contained kits designed to be used anywhere - at home, in classrooms, in libraries, or during after-school programs. Open the box, and the discovery begins."
          />
        </div>
      </section>

      {/* 4 · Product Showcase */}
      <section id="kits" data-testid="steam-showcase" className="bg-[#0E0E0E] text-white py-24 md:py-32 scroll-mt-24">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#DCE842] font-black mb-4">Featured Lineup</div>
              <h2 className="font-display font-black text-4xl md:text-6xl leading-[1.02] tracking-tight text-white">
                STEAM-2-Go™ Discovery Kits.
              </h2>
            </div>
            <p className="max-w-md text-white/70 font-light text-base md:text-lg">
              Four self-contained kits, each pairing a real scientific discipline with a Black, Indigenous, Latinx, or
              women trailblazer children rarely meet in a textbook.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KitCard
              testId="kit-earth-day"
              slug="earth-day-native-tree"
              tag="Ecology"
              tint="#DCE842"
              icon={<Trees size={72} strokeWidth={1.5} />}
              title="Earth Day Native Tree &amp; Forest Canopy"
              tagline="Plant the seeds of environmental leadership."
              blurb="Explore soil chemistry, germinate native seeds, build an elevation clinometer, and create technical botanical sketches worthy of a field journal."
              trailblazer="Dr. Wangari Maathai &amp; George Washington Carver"
            />
            <KitCard
              testId="kit-rhythms"
              slug="rhythms-and-resonance"
              tag="Acoustics"
              tint="#EC008C"
              icon={<Music size={72} strokeWidth={1.5} className="text-white" />}
              title="Rhythms &amp; Resonance"
              tagline="Discover the physics of sound through custom music."
              blurb="Investigate sound frequencies, assemble a wooden resonator box, and construct a functional 5-key kalimba tuned by the young engineer building it."
              trailblazer="Dr. James E. West"
            />
            <KitCard
              testId="kit-bridges"
              slug="urban-architects-bridges"
              tag="Engineering"
              tint="#F3E30A"
              icon={<Building2 size={72} strokeWidth={1.5} />}
              title="Urban Architects &amp; Structural Bridges"
              tagline="Design the cities of tomorrow."
              blurb="Master load capacity, draft scale blueprints, and construct truss and suspension bridges from wood dowels and cardstock - then stress-test them."
              trailblazer="Norma Sklarek"
            />
            <KitCard
              testId="kit-solar"
              slug="solar-trackers-sculptures"
              tag="Renewable"
              tint="#DCE842"
              icon={<SunIcon size={72} strokeWidth={1.5} />}
              title="Solar Trackers &amp; Kinetic Sculptures"
              tagline="Harvest light to create motion."
              blurb="Wire low-voltage solar cells, build a gear-driven sun-tracking mechanism, and craft light-refracting whirligigs that spin from pure sunlight."
              trailblazer="Lonnie Johnson"
            />
          </div>
        </div>
      </section>

      {/* Real-world proof gallery */}
      <section data-testid="steam-gallery" className="container-x py-24 md:py-32">
        <div className="max-w-3xl mb-12">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-black mb-4">In the wild</div>
          <h2 className="font-display font-black text-4xl md:text-5xl leading-[1.02] text-black tracking-tight">
            From our workshop to classrooms nationwide.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { src: "/steam/kits-lineup.jpg", label: "Made to order" },
            { src: "/steam/school-visit.jpg", label: "MLK Jr. Elementary" },
            { src: "/steam/kit-inside.jpg", label: "What's inside" },
            { src: "/steam/packing.jpg", label: "Kit assembly day" },
          ].map((g, i) => (
            <div key={i} className="relative aspect-[4/5] overflow-hidden border-2 border-black group">
              <img src={g.src} alt={g.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
              <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-white text-[10px] tracking-[0.24em] uppercase font-black px-3 py-2">
                {g.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 5 · Dual CTA */}
      <section data-testid="steam-dual-cta" className="border-t-2 border-black">
        <div className="grid md:grid-cols-2">
          {/* Parents */}
          <div className="bg-[#EC008C] text-white p-10 md:p-16 border-b-2 md:border-b-0 md:border-r-2 border-black">
            <HomeIcon size={32} className="mb-5" strokeWidth={2.5} />
            <div className="text-[11px] tracking-[0.28em] uppercase font-black mb-3 text-[#F3E30A]">For Parents &amp; Caregivers</div>
            <h3 className="font-display font-black text-3xl md:text-4xl leading-[1.05] mb-5 text-white">
              Give your child the confidence to say: <span className="italic">&ldquo;I am an innovator.&rdquo;</span>
            </h3>
            <p className="text-white/90 font-light text-base md:text-lg leading-relaxed max-w-md">
              Looking for screen-free enrichment that keeps your child engaged while building real-world scientific
              skills? Our kits bring hands-on learning directly to your kitchen table.
            </p>
            <Link
              to="/shop?category=steam-kits"
              data-testid="steam-parent-cta"
              className="mt-8 inline-flex items-center gap-2 bg-white text-[#EC008C] px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-white hover:bg-black hover:text-white hover:border-black transition-colors"
            >
              Shop Individual Kits <ArrowRight size={14} />
            </Link>
          </div>

          {/* Educators */}
          <div className="bg-black text-white p-10 md:p-16">
            <GraduationCap size={32} className="mb-5 text-[#DCE842]" strokeWidth={2.5} />
            <div className="text-[11px] tracking-[0.28em] uppercase font-black mb-3 text-[#DCE842]">For Educators, Districts &amp; Organizations</div>
            <h3 className="font-display font-black text-3xl md:text-4xl leading-[1.05] mb-5 text-white">
              Bring turnkey STEAM enrichment to your classrooms &amp; programs.
            </h3>
            <p className="text-white/80 font-light text-base md:text-lg leading-relaxed max-w-md">
              Fully aligned with Next Generation Science Standards (NGSS) and eligible for Title I, Title IV, ESSER, and
              21st CCLC federal funding. We offer volume discounts, teacher guides, and custom rollout support.
            </p>
            <a
              href="/steam/steam-2-go-grant-catalog.pdf"
              download="MAKEBA-STEAM-2-Go-Grant-Catalog.pdf"
              target="_blank"
              rel="noreferrer"
              data-testid="steam-educator-cta"
              className="mt-8 inline-flex items-center gap-2 bg-[#DCE842] text-black px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-[#DCE842] hover:bg-white hover:border-white transition-colors"
            >
              Request a Quote / Sample Kit <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* 6 · Footer tagline strip */}
      <section data-testid="steam-tagline" className="bg-[#DCE842] py-16 md:py-20 border-t-2 border-black">
        <div className="container-x text-center max-w-4xl mx-auto">
          <Award size={28} className="mx-auto mb-5 text-black" strokeWidth={2.5} />
          <div className="font-display font-black text-3xl md:text-4xl text-black leading-tight">
            Makeba Design & Prints - Inspiring Curiosity, Confidence, and Representation in STEAM.
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-3 text-[11px] tracking-[0.24em] uppercase font-black text-black">
            <Link to="/about" className="hover:text-[#EC008C] transition-colors">About Our Mission</Link>
            <span className="opacity-30">·</span>
            <Link to="/contact" className="hover:text-[#EC008C] transition-colors">District &amp; Grant Procurement</Link>
            <span className="opacity-30">·</span>
            <Link to="/contact" className="hover:text-[#EC008C] transition-colors">Bulk Orders</Link>
            <span className="opacity-30">·</span>
            <Link to="/contact" className="hover:text-[#EC008C] transition-colors">Contact Us</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Steam;
